import { getFunctions, httpsCallable } from 'firebase/functions';
import { User } from '../models/User';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, UserCredential } from 'firebase/auth';
import { auth } from '../config/firebase.ts';
import { logLoginActivity } from '../data/adminActivityDatabase.ts';
import { syncUserData } from './auth';

/**
 * Detect if popups are blocked by attempting to open a test popup
 * @returns Promise<boolean> true if popups are blocked, false if allowed
 */
export const detectPopupBlocking = async (): Promise<boolean> => {
  try {
    // Attempt to open a blank popup
    const popup = window.open('', 'popup-test', 'width=1,height=1,left=-1000,top=-1000');
    
    if (!popup) {
      return true; // Popup was blocked
    }
    
    if (popup.closed) {
      return true; // Popup was immediately closed (blocked)
    }
    
    // Check if popup actually opened after a short delay
    return new Promise((resolve) => {
      setTimeout(() => {
        if (popup.closed || !popup.location) {
          resolve(true); // Popup was blocked or closed
        } else {
          popup.close(); // Clean up test popup
          resolve(false); // Popup worked
        }
      }, 100);
    });
  } catch (error) {
    return true; // Error opening popup indicates blocking
  }
};

/**
 * Create a consistent popup blocked error message
 * @returns string The error message to display
 */
export const getPopupBlockedMessage = (): string => {
  return 'Please allow popups for this site to sign in with Google. Check your browser settings or popup blocker and try again.';
};

// Dummy implementations for frontend (do NOT use for real security)
export const hashPassword = async (password: string): Promise<string> => {
  return password; // No hashing in frontend
};

export const verifyPassword = async (password: string, hashedPassword: string): Promise<boolean> => {
  return password === hashedPassword;
};

/**
 * Invite a user to create their Firebase Auth login
 * @param userId The ID of the user to invite
 * @param adminUserId The ID of the admin sending the invite
 * @param email The user's email address
 * @param fullName The user's full name
 * @returns Promise<void>
 */
export const inviteUserToLogin = async (
  userId: string,
  adminUserId: string,
  email: string,
  fullName: string
): Promise<void> => {
  const functions = getFunctions();
  const inviteUser = httpsCallable(functions, 'inviteUserToLogin');

  try {
    await inviteUser({
      userId,
      adminUserId,
      email,
      fullName
    });
  } catch (error) {
    console.error('Failed to invite user to login:', error);
    throw error;
  }
};

/**
 * Send a password reset link to a user who already has Firebase Auth setup
 * @param userId The ID of the user to send reset link to
 * @param adminUserId The ID of the admin sending the reset link
 * @param email The user's email address
 * @param fullName The user's full name
 * @returns Promise<void>
 */
export const sendPasswordResetLink = async (
  userId: string,
  adminUserId: string,
  email: string,
  fullName: string
): Promise<void> => {
  const functions = getFunctions();
  const sendResetLink = httpsCallable(functions, 'sendPasswordResetLink');

  try {
    await sendResetLink({
      userId,
      adminUserId,
      email,
      fullName
    });
  } catch (error) {
    console.error('Failed to send password reset link:', error);
    throw error;
  }
};

/**
 * Validate an invitation token and create a Firebase Auth user
 * @param token The invitation token
 * @param password The password for the new user
 * @returns Promise<{ success: boolean; firebaseUid: string }>
 */
export const validateInvitationAndCreateUser = async (
  token: string,
  password: string
): Promise<{ success: boolean; firebaseUid: string }> => {
  const functions = getFunctions();
  const validateInvitation = httpsCallable(functions, 'validateInvitationAndCreateUser');

  try {
    const result = await validateInvitation({
      token,
      password
    });
    return result.data as { success: boolean; firebaseUid: string };
  } catch (error) {
    console.error('Failed to validate invitation and create user:', error);
    throw error;
  }
};

/**
 * Sign in with email and password and log the activity
 */
export const signInWithEmailAndPasswordWithLogging = async (email: string, password: string): Promise<UserCredential> => {
  console.log('🔐 signInWithEmailAndPasswordWithLogging called for:', email);
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log('✅ Email login successful, logging activity');
    
    // Sync user data to get the proper user info for logging
    try {
      const userData = await syncUserData(result.user);
      await logLoginActivity(userData.id, userData.fullName, true);
      console.log('✅ Login activity logged for user:', userData.fullName);
    } catch (syncError) {
      console.error('Failed to sync user data or log login activity:', syncError);
      // Still log basic login info even if sync fails
      await logLoginActivity(result.user.uid, result.user.email || 'Unknown', true);
      console.log('✅ Basic login activity logged for:', result.user.email);
    }
    
    return result;
  } catch (error: any) {
    console.log('❌ Email login failed, logging failure');
    // Log failed login attempt
    await logLoginActivity(email, email, false, error.message);
    throw error;
  }
};

/**
 * Sign in with Google and log the activity
 */
export const signInWithGoogleWithLogging = async (): Promise<UserCredential> => {
  console.log('🔐 signInWithGoogleWithLogging called');
  
  // Check for popup blocking before attempting sign-in
  const isPopupBlocked = await detectPopupBlocking();
  if (isPopupBlocked) {
    console.log('❌ Popups are blocked - throwing popup-blocked error');
    const error = new Error(getPopupBlockedMessage());
    (error as any).code = 'auth/popup-blocked';
    throw error;
  }
  
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    console.log('✅ Google login successful, logging activity');
    
    // Sync user data to get the proper user info for logging
    try {
      const userData = await syncUserData(result.user);
      await logLoginActivity(userData.id, userData.fullName, true);
      console.log('✅ Login activity logged for user:', userData.fullName);
    } catch (syncError) {
      console.error('Failed to sync user data or log login activity:', syncError);
      // Still log basic login info even if sync fails
      await logLoginActivity(result.user.uid, result.user.email || 'Unknown', true);
      console.log('✅ Basic login activity logged for:', result.user.email);
    }
    
    return result;
  } catch (error: any) {
    console.log('❌ Google login failed, logging failure');
    
    // Enhanced popup blocking detection
    if (error.code === 'auth/popup-blocked' || 
        error.code === 'auth/cancelled-popup-request' ||
        error.message.includes('popup') ||
        error.message.includes('blocked')) {
      // Create a specific popup blocked error
      const popupError = new Error(getPopupBlockedMessage());
      (popupError as any).code = 'auth/popup-blocked';
      throw popupError;
    }
    
    // Log failed login attempt
    const email = error.customData?.email || 'Unknown';
    await logLoginActivity(email, email, false, error.message);
    throw error;
  }
}; 